/**
 * Dice Component
 * Configurable dice with different side counts and visual representations
 */

import { useState, useEffect, useRef } from 'react';

/**
 * Roll a single die
 * @param {number} sides - Number of sides (default 6)
 * @returns {number} - Roll result (1 to sides)
 */
export const rollDie = (sides = 6) => {
    return Math.floor(Math.random() * sides) + 1;
};

/**
 * Roll multiple dice
 * @param {number} count - Number of dice
 * @param {number} sides - Sides per die
 * @returns {{dice: number[], total: number}}
 */
export const rollDice = (count = 1, sides = 6) => {
    const dice = [];
    for (let i = 0; i < count; i++) {
        dice.push(rollDie(sides));
    }
    return {
        dice,
        total: dice.reduce((sum, d) => sum + d, 0)
    };
};

// SVG dice face patterns (dot positions for d6, 1-6)
const D6_DOTS = {
    1: [[50, 50]],
    2: [[30, 30], [70, 70]],
    3: [[30, 30], [50, 50], [70, 70]],
    4: [[30, 30], [70, 30], [30, 70], [70, 70]],
    5: [[30, 30], [70, 30], [50, 50], [30, 70], [70, 70]],
    6: [[30, 30], [70, 30], [30, 50], [70, 50], [30, 70], [70, 70]]
};

// Color schemes for different dice types
const DICE_COLORS = {
    4: { bg: '#E74C3C', text: '#FFFFFF', border: '#C0392B' },   // Red
    6: { bg: '#FFFFFF', text: '#1F2937', border: '#E5E7EB' },   // White (classic)
    8: { bg: '#3498DB', text: '#FFFFFF', border: '#2980B9' },   // Blue
    10: { bg: '#9B59B6', text: '#FFFFFF', border: '#8E44AD' },  // Purple
    12: { bg: '#27AE60', text: '#FFFFFF', border: '#1E8449' },  // Green
    20: { bg: '#F39C12', text: '#FFFFFF', border: '#D68910' }   // Orange
};

/**
 * D4 Face (Triangle/Tetrahedron)
 */
const D4Face = ({ value, size = 60 }) => {
    const colors = DICE_COLORS[4];
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-md">
            <polygon
                points="50,10 90,85 10,85"
                fill={colors.bg}
                stroke={colors.border}
                strokeWidth={3}
            />
            <text
                x="50"
                y="60"
                textAnchor="middle"
                fill={colors.text}
                fontSize="28"
                fontWeight="bold"
                fontFamily="sans-serif"
            >
                {value}
            </text>
        </svg>
    );
};

/**
 * D6 Face (Standard cube with dots)
 */
const D6Face = ({ value, size = 60 }) => {
    const dots = D6_DOTS[value] || [];
    const dotRadius = 8;
    const colors = DICE_COLORS[6];

    return (
        <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-md">
            <rect
                x={5}
                y={5}
                width={90}
                height={90}
                rx={12}
                ry={12}
                fill={colors.bg}
                stroke={colors.border}
                strokeWidth={2}
            />
            {dots.map(([cx, cy], index) => (
                <circle
                    key={index}
                    cx={cx}
                    cy={cy}
                    r={dotRadius}
                    fill={colors.text}
                />
            ))}
        </svg>
    );
};

/**
 * D8 Face (Diamond/Octahedron)
 */
const D8Face = ({ value, size = 60 }) => {
    const colors = DICE_COLORS[8];
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-md">
            <polygon
                points="50,5 95,50 50,95 5,50"
                fill={colors.bg}
                stroke={colors.border}
                strokeWidth={3}
            />
            <text
                x="50"
                y="58"
                textAnchor="middle"
                fill={colors.text}
                fontSize="28"
                fontWeight="bold"
                fontFamily="sans-serif"
            >
                {value}
            </text>
        </svg>
    );
};

/**
 * D10 Face (Kite shape)
 */
const D10Face = ({ value, size = 60 }) => {
    const colors = DICE_COLORS[10];
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-md">
            <polygon
                points="50,5 90,40 75,95 25,95 10,40"
                fill={colors.bg}
                stroke={colors.border}
                strokeWidth={3}
            />
            <text
                x="50"
                y="60"
                textAnchor="middle"
                fill={colors.text}
                fontSize="24"
                fontWeight="bold"
                fontFamily="sans-serif"
            >
                {value}
            </text>
        </svg>
    );
};

/**
 * D12 Face (Pentagon)
 */
const D12Face = ({ value, size = 60 }) => {
    const colors = DICE_COLORS[12];
    // Pentagon points
    const cx = 50, cy = 50, r = 42;
    const points = [];
    for (let i = 0; i < 5; i++) {
        const angle = (i * 72 - 90) * Math.PI / 180;
        points.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
    }

    return (
        <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-md">
            <polygon
                points={points.join(' ')}
                fill={colors.bg}
                stroke={colors.border}
                strokeWidth={3}
            />
            <text
                x="50"
                y="58"
                textAnchor="middle"
                fill={colors.text}
                fontSize="24"
                fontWeight="bold"
                fontFamily="sans-serif"
            >
                {value}
            </text>
        </svg>
    );
};

/**
 * D20 Face (Triangle/Icosahedron face)
 */
const D20Face = ({ value, size = 60 }) => {
    const colors = DICE_COLORS[20];
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-md">
            <polygon
                points="50,8 95,88 5,88"
                fill={colors.bg}
                stroke={colors.border}
                strokeWidth={3}
            />
            <text
                x="50"
                y="65"
                textAnchor="middle"
                fill={colors.text}
                fontSize="22"
                fontWeight="bold"
                fontFamily="sans-serif"
            >
                {value}
            </text>
        </svg>
    );
};

/**
 * Generic Die Face - selects the right component based on sides
 */
const DieFace = ({ value, sides = 6, size = 60 }) => {
    switch (sides) {
        case 4:
            return <D4Face value={value} size={size} />;
        case 6:
            return <D6Face value={value} size={size} />;
        case 8:
            return <D8Face value={value} size={size} />;
        case 10:
            return <D10Face value={value} size={size} />;
        case 12:
            return <D12Face value={value} size={size} />;
        case 20:
            return <D20Face value={value} size={size} />;
        default:
            // Fallback: simple square with number
            return (
                <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-md">
                    <rect
                        x={5}
                        y={5}
                        width={90}
                        height={90}
                        rx={12}
                        ry={12}
                        fill="#6B7280"
                        stroke="#4B5563"
                        strokeWidth={2}
                    />
                    <text
                        x="50"
                        y="58"
                        textAnchor="middle"
                        fill="#FFFFFF"
                        fontSize="28"
                        fontWeight="bold"
                        fontFamily="sans-serif"
                    >
                        {value}
                    </text>
                </svg>
            );
    }
};

/**
 * Single Die Component (for displaying one die)
 */
export const SingleDie = ({
    value,
    sides = 6,
    size = 60,
    isRolling = false,
    onClick,
    disabled = false
}) => {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`
                p-1 rounded-lg transition-all
                ${disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:scale-105 active:scale-95 cursor-pointer'
                }
                ${isRolling ? 'animate-bounce' : ''}
            `}
            aria-label={`Die showing ${value}`}
        >
            <DieFace value={value} sides={sides} size={size} />
        </button>
    );
};

/**
 * Animation speed presets (in milliseconds per frame)
 */
const ANIMATION_SPEEDS = {
    fast: { frameMs: 50, frames: 6 },
    normal: { frameMs: 80, frames: 8 },
    slow: { frameMs: 120, frames: 12 }
};

/**
 * Dice Component - shows multiple dice and handles rolling
 */
export const Dice = ({
    onRoll,
    disabled = false,
    lastResult = null,  // { dice: [3, 5], total: 8 }
    diceCount = 1,
    diceSides = 6,
    animationSpeed = 'normal'  // 'fast', 'normal', 'slow'
}) => {
    const [isRolling, setIsRolling] = useState(false);
    const [displayValues, setDisplayValues] = useState(
        lastResult?.dice || Array(diceCount).fill(null)
    );

    // Track previous config to detect changes
    const prevConfigRef = useRef({ diceCount, diceSides });

    // Update display when dice configuration changes (not during rolling)
    useEffect(() => {
        const prevConfig = prevConfigRef.current;
        if (prevConfig.diceCount !== diceCount || prevConfig.diceSides !== diceSides) {
            setDisplayValues(Array(diceCount).fill(null));
            prevConfigRef.current = { diceCount, diceSides };
        }
    }, [diceCount, diceSides]);

    // Get animation config
    const animConfig = ANIMATION_SPEEDS[animationSpeed] || ANIMATION_SPEEDS.normal;

    const handleRoll = () => {
        if (disabled || isRolling) return;

        setIsRolling(true);

        // Animate rolling effect
        let rollCount = 0;
        const interval = setInterval(() => {
            // Generate random values for each die during animation
            const animValues = Array(diceCount).fill(0).map(() => rollDie(diceSides));
            setDisplayValues(animValues);
            rollCount++;

            if (rollCount >= animConfig.frames) {
                clearInterval(interval);
                const result = rollDice(diceCount, diceSides);
                setDisplayValues(result.dice);
                setIsRolling(false);
                onRoll(result);
            }
        }, animConfig.frameMs);
    };

    // Calculate die size based on count
    const getDieSize = () => {
        if (diceCount === 1) return 70;
        if (diceCount === 2) return 55;
        return 45;
    };

    const dieSize = getDieSize();

    return (
        <div className="flex flex-col items-center gap-3">
            {/* Dice Display */}
            <div className="flex items-center gap-2 flex-wrap justify-center">
                {displayValues.map((value, index) => (
                    <SingleDie
                        key={index}
                        value={value || '?'}
                        sides={diceSides}
                        size={dieSize}
                        isRolling={isRolling}
                        onClick={handleRoll}
                        disabled={disabled || isRolling}
                    />
                ))}
            </div>

            {/* Total Display (only show for multiple dice) */}
            {diceCount > 1 && displayValues.some(v => v !== null) && !isRolling && (
                <div className="text-lg font-bold text-gray-700">
                    Total: {displayValues.reduce((sum, v) => sum + (v || 0), 0)}
                </div>
            )}

            {/* Roll Button */}
            <button
                onClick={handleRoll}
                disabled={disabled || isRolling}
                className={`
                    px-6 py-2 rounded-lg font-medium transition-colors
                    ${disabled
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-badger-purple text-white hover:bg-purple-700'
                    }
                `}
            >
                {isRolling ? 'Rolling...' : `Roll ${diceCount}d${diceSides}`}
            </button>

            {/* Last Roll Info */}
            {lastResult && !isRolling && (
                <div className="text-sm text-gray-500">
                    Last roll: <span className="font-medium text-gray-700">
                        {lastResult.dice.join(' + ')} = {lastResult.total}
                    </span>
                </div>
            )}
        </div>
    );
};

export default Dice;
